import React, { lazy, Suspense, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

import { BasicData } from "@App";
import { liteItineraryBlocks } from "@configs/layoutConfigs";
import { isLite, getItineraryIdentifier } from '@utils/Utils';
import { useDispatch, useSelector } from "react-redux";
import { fetchBodyData, setBodyData } from '@utils/redux/slices/bodyDataSlice';
import { useGlobalEnvConfig } from "@environment/GlobalEnvConfigProvider";

import "./style.scss";

// Components
import SpinnerComponent from "@components/spinner";
import CoverPage from "@components/cover-page";
import NavBarComponent from "@components/nav-bar";
import BodyContainer from "@containers/body";
import SectionContainer from "@containers/section";
import Contact from "@components/contact";
import BackToTop from "@components/back-to-top";
import TripOverview from "@components/trip-summary/overview";
import PreviewBanner from "@components/preview-banner";

// Icons

// Lazy loaded Components
const EnquiryForm = lazy(() => import("@components/enquiry-form"));
const IntroductionComponent = lazy(() => import("@components/trip-summary/introduction"));
const ScheduledDepraturesComponent = lazy(() => import("@components/trip-summary/scheduled-departures"));
const FastFactsComponent = lazy(() => import("@components/trip-summary/fast-facts"));
const AccommodationComponent = lazy(() => import("@components/trip-summary/accommodation"));
const TransportComponent = lazy(() => import("@components/trip-summary/transport"));
const InteractiveMapWrap = lazy(() => import("@components/interactive-map-wrapper"));
const DailySliderComponent = lazy(() => import("@components/daily-info/slider"));
const PricingComponent = lazy(() => import("@components/trip-summary/pricing"));
const DocumentsComponent = lazy(() => import("@components/trip-summary/documents"));
const TermsAndConditionsComponent = lazy(() => import("@components/terms-and-conditions"));
const TravelGuidanceComponent = lazy(() => import("@components/travel-guidance"));
const FooterComponent = lazy(() => import("@components/footer"));

const LayoutComponent = ({ template }) => {

  const basicData = useContext(BasicData);
  const bodyData = useSelector((state) => state.bodyData.data);
  const error = useSelector(state => state.bodyData.error);
  const [isPreview, setIsPreview] = useState(false);
  const [previewOffset, setPreviewOffset] = useState(0);

  const previewBannerRef = useRef(null);

  const { documents, summary, price, price_includes, price_excludes, scheduled_departures, trip_summary } = useContext(BasicData);
  const { REACT_APP_API_DOMAIN } = useGlobalEnvConfig();
  const showPricing = price || price_includes || price_excludes;
  const dispatch = useDispatch();

  const inIframe = () => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  };

  useEffect(() => {
    if (!bodyData) {
      const itineraryIdentifier = getItineraryIdentifier();
      if (itineraryIdentifier)
        dispatch(fetchBodyData({ id: itineraryIdentifier, domain: REACT_APP_API_DOMAIN }));

    } else {
      dispatch(setBodyData(bodyData));
    }
  }, [bodyData, dispatch, REACT_APP_API_DOMAIN]);

  if (error) {
    throw error;
  }
  
  useEffect(() => {
    if (!inIframe() && window.location.search.includes('?m=d')) {
      setIsPreview(true);
    }
  }, []);

  useLayoutEffect(() => {
    if (isPreview && previewBannerRef.current) {
      const rect = previewBannerRef.current.getBoundingClientRect();
      setPreviewOffset(rect.height);
    }
  }, [isPreview]);

  const getItineraryBlockComponent = (block, i) => {
    switch (block.Type) {
      case "Introduction":
        if (summary)
          return (
            <Suspense fallback={<SpinnerComponent />}>
              <IntroductionComponent />
            </Suspense>
          );
        else return;
      case "ScheduledDepartures":
        if (scheduled_departures)
          return (
            <Suspense fallback={<SpinnerComponent />}>
              <ScheduledDepraturesComponent />
            </Suspense>
          );
        else return;
      case "FastFacts":
        return (
          <Suspense fallback={<SpinnerComponent />}>
            <FastFactsComponent />
          </Suspense>
        );
      case "TripSummary":
        return (
          <div id="accommodation-summary">
            <Suspense fallback={<SpinnerComponent />}>
              <AccommodationComponent />
            </Suspense>
          </div>
        );
      case "TripOverview":
        if (bodyData)
          return (
            <Suspense fallback={<SpinnerComponent />}>
              <TripOverview />
            </Suspense>
          );
        else return <SpinnerComponent />;
      case "InteractiveMap":
        if (basicData)
          return (
            <Suspense fallback={<SpinnerComponent />}>
              <InteractiveMapWrap basicData={basicData} />
            </Suspense>
          );
        else return;
      case "TransportSummary":
        return (
          <Suspense fallback={<SpinnerComponent />}>
            <TransportComponent />
          </Suspense>
        );
      case "Pricing":
        if (showPricing)
          return (
            <Suspense fallback={<SpinnerComponent />}>
              <PricingComponent />
            </Suspense>
          );
        else return;
      case "Documents":
        if (documents)
          return (
            <Suspense fallback={<SpinnerComponent />}>
              <DocumentsComponent />
            </Suspense>
          );
        else return;
      case "DayByDaySlider":
        if (bodyData)
          return (
            <Suspense fallback={<SpinnerComponent />}>
              <SectionContainer className="daily-info">
                <DailySliderComponent legs={trip_summary.legs} />
              </SectionContainer>
            </Suspense>
          );
        else return <SpinnerComponent />;
      case "DayByDaySummary":
        return <BodyContainer />;
      case "Enquiry":
        return (
          <Suspense fallback={<SpinnerComponent />}>
            <EnquiryForm />
          </Suspense>
        );
      case "TravelGuidance":
        if (basicData?.travel_guidance || basicData?.branding?.travel_guidance)
          return (
            <Suspense fallback={<SpinnerComponent />}>
              <TravelGuidanceComponent />
            </Suspense>
          );
        else return;
      case "TermsAndConditions":
        if (basicData?.terms_and_conditions || basicData?.branding?.terms_and_conditions)
          return (
            <Suspense fallback={<SpinnerComponent />}>
              <TermsAndConditionsComponent />
            </Suspense>
          );
        else return;
      default:
        return;
    }
  };

  const itineraryBlockRender = (block, i) => {
    return (
      <div key={i}>
        {getItineraryBlockComponent(block, i)}
      </div>
    );
  };

  const determineTierBlocks = (tier) => {
    if (isLite(basicData.operator_tier)) {
      return liteItineraryBlocks.map(itineraryBlockRender);
    } else {
      return template?.ItineraryBlocks.map(itineraryBlockRender);
    }
  }

  return (

    <>
      {isPreview && <PreviewBanner ref={previewBannerRef} />}

      <div style={isPreview ? { marginTop: previewOffset } : {}} className="body bg-white">

        <NavBarComponent />
        <CoverPage itineraryDisabled={false} />

        {/* Blocks */}
        {determineTierBlocks(basicData.operator_tier)}
        {/* Blocks */}

        <Suspense fallback={<SpinnerComponent />}>
          <FooterComponent />
        </Suspense>

        {/* Settings */}
        <div className="floating-elements">
          {template?.IsContactFABVisible && basicData?.branding && <Contact />}
          <BackToTop />
        </div>
        {/* Settings */}
      </div>
    </>
  );
};

export default LayoutComponent;
